// groupCreate.mjs — 第八十五批：建组表单与工时设置表单的纯判定。
//
// 需求方两条裁决落到呈现层的部分：
//   ① 项目组应该可以在运行期创建；
//   ② 没有合理理由与部署行为绑定的配置，走管理端点。
//
// 为什么这些判定要抽成纯模块、而不是写在 JSX 的 disabled={…} 里：
// 表单的"能不能按"就是**规则在用户面前的样子**。写在 JSX 里它只能被点击测到，
// 而 R-01「窗口必须是正奇数」这种规则的边界（0、偶数、负数、非整数、超长 slug）
// 有十几个，逐个去点是不现实的——于是实际发生的事情是没人测，然后某一天
// 一个偶数被送到后端，用户拿到一句看不懂的 400。
//
// **与后端同源但不共享代码**：后端 `valid_project_group_slug`（route_util.cpp）是
// 权威闸，这里是同一条规则的前置副本。两份手写副本必然漂移——所以这里的注释与
// 用例都点名"后端才是权威"，前端这一份的职责只有一个：**在按下按钮之前就说清楚
// 哪里不对**，而不是替后端把关。后端拒绝时前端照样要显示那句拒绝。

// ── slug ────────────────────────────────────────────────────────────────────
// 允许集与后端逐字对齐：`[a-z0-9]`，中间可有单个 `-`，2–40 字符。
// 不含点，故 `..` 与前导点一并挡掉——slug 会成为发布目录的一段。
export function slugProblem(raw) {
  const s = String(raw ?? '');
  if (s.length === 0) return 'empty';
  if (s.length < 2 || s.length > 40) return 'length';
  if (/[A-Z]/.test(s)) return 'uppercase';        // 单独一档：这是最常见的手误
  if (!/^[a-z0-9-]+$/.test(s)) return 'charset';
  if (s.startsWith('-') || s.endsWith('-')) return 'hyphenEdge';
  if (s.includes('--')) return 'hyphenDouble';
  return null;
}

// 从中文名推一个 slug 建议是做不到的（中文没有音译规则可依），故只在**英文名**
// 上推：小写、非允许字符压成连字符、收尾清理。推不出来就返回空串，让人自己填——
// 猜一个错的比不猜更糟，因为 slug 一旦建出来就是目录名。
export function suggestSlug(displayNameEn) {
  const s = String(displayNameEn ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 40)
    .replace(/-+$/g, '');            // 截断可能又切出一个尾连字符
  return slugProblem(s) === null ? s : '';
}

// 两个名字都必填：只收一个再复制到另一列，等于把中文名钉进英文列，而目录页
// 照列名渲染（第四十三批语言规则）。80 字节上限与后端一致。
export function nameProblem(raw) {
  const s = String(raw ?? '').trim();
  if (s.length === 0) return 'empty';
  // 后端量的是**字节**（80 bytes），中文一个字三字节——前端也量字节，
  // 否则一个 30 字的中文名在前端看着合法、到后端被拒。
  if (new TextEncoder().encode(s).length > 80) return 'tooLong';
  return null;
}

export function groupFormState({ slug, displayName, displayNameEn }) {
  const problems = {
    slug: slugProblem(slug),
    displayName: nameProblem(displayName),
    displayNameEn: nameProblem(displayNameEn),
  };
  return { problems, canSubmit: Object.values(problems).every((p) => p === null) };
}

// ── 工时两参数 ──────────────────────────────────────────────────────────────
// R-01：录入窗口必须是**正奇数**。奇数不是洁癖——窗口是"今天居中的 N 天"，
// 偶数没有中心，所以这条规则是几何上的而不是约定上的。
export function windowDaysProblem(raw) {
  if (raw === '' || raw === null || raw === undefined) return 'empty';
  const n = Number(raw);
  if (!Number.isInteger(n)) return 'notInteger';
  if (n < 1) return 'notPositive';
  if (n % 2 === 0) return 'notOdd';
  return null;
}

// R-32：工作日起点小时 0..23。
export function dayStartHourProblem(raw) {
  if (raw === '' || raw === null || raw === undefined) return 'empty';
  const n = Number(raw);
  if (!Number.isInteger(n)) return 'notInteger';
  if (n < 0 || n > 23) return 'outOfRange';
  return null;
}

// 表单只提交**改动过**的字段。全都没改就不该能按——一次什么都不改的 POST 会
// 在审计里留下一条"配置变更"，而那条记录说的不是真的。
export function workHoursFormState({ windowDays, dayStartHour, current }) {
  const problems = {
    windowDays: windowDaysProblem(windowDays),
    dayStartHour: dayStartHourProblem(dayStartHour),
  };
  const changed = {
    windowDays: problems.windowDays === null && Number(windowDays) !== current?.workLogWindowDays,
    dayStartHour: problems.dayStartHour === null && Number(dayStartHour) !== current?.workDayStartHour,
  };
  const anyChanged = changed.windowDays || changed.dayStartHour;
  const allValid = Object.values(problems).every((p) => p === null);
  const payload = {};
  if (changed.windowDays) payload.workLogWindowDays = Number(windowDays);
  if (changed.dayStartHour) payload.workDayStartHour = Number(dayStartHour);
  return { problems, changed, canSubmit: allValid && anyChanged, payload };
}
